<script>
  import { router } from '@inertiajs/svelte'
  import { tick } from 'svelte'
  import Button from '@/components/ui/Button.svelte'
  import Card from '@/components/ui/Card.svelte'
  import Input from '@/components/ui/Input.svelte'
  import { generateNickname } from '@/utils/nicknameGenerator'

  let { errors = {}, flash = {} } = $props()

  let form = $state({
    name: '',
    nickname: generateNickname(),
    email: '',
    password: '',
    password_confirmation: '',
    work_email: '',
    organization_name: '',
    department: '',
    team: '',
    position: ''
  })

  let isSubmitting = $state(false)
  let step = $state(1)

  // 닉네임 중복 체크 상태
  let nicknameStatus = $state('idle') // 'idle' | 'checking' | 'available' | 'taken'
  let nicknameSuggestion = $state(null)
  let nicknameTimer = null

  // 닉네임 변경 시 debounce 체크
  $effect(() => {
    const nickname = form.nickname
    if (!nickname || nickname.trim().length < 2) {
      nicknameStatus = 'idle'
      nicknameSuggestion = null
      return
    }
    nicknameStatus = 'checking'
    nicknameSuggestion = null
    clearTimeout(nicknameTimer)
    nicknameTimer = setTimeout(() => checkNickname(nickname.trim()), 400)
  })

  async function checkNickname(nickname) {
    try {
      const res = await fetch(`/api/nicknames/check?nickname=${encodeURIComponent(nickname)}`)
      const data = await res.json()
      // 비동기 사이에 닉네임이 바뀌었으면 무시
      if (form.nickname.trim() !== nickname) return
      if (data.available) {
        nicknameStatus = 'available'
        nicknameSuggestion = null
      } else {
        nicknameStatus = 'taken'
        nicknameSuggestion = data.suggestion
      }
    } catch {
      nicknameStatus = 'idle'
    }
  }

  function refreshNickname() {
    form.nickname = generateNickname()
  }

  function applySuggestion() {
    form.nickname = nicknameSuggestion
  }

  function nextStep() {
    if (form.name && form.nickname && form.email && form.password && form.password === form.password_confirmation && nicknameStatus === 'available') {
      step = 2
    }
  }

  function prevStep() {
    step = 1
  }

  function handleSubmit(e) {
    e.preventDefault()
    isSubmitting = true
    router.post('/register', form, {
      onFinish: () => { isSubmitting = false }
    })
  }

  let canProceed = $derived(
    !!form.name && !!form.nickname && !!form.email &&
    !!form.password && form.password === form.password_confirmation &&
    nicknameStatus === 'available'
  )
</script>

<div class="min-h-screen bg-gray-50 dark:bg-dark-primary flex items-center justify-center px-4 py-12">
  <div class="max-w-md w-full">
    <!-- Logo & Title -->
    <div class="text-center mb-8">
      <h1 class="text-h1 text-content mb-2">AX Hub</h1>
      <p class="text-body2 text-content-tertiary">AI 전문가 커뮤니티에 가입하세요</p>
    </div>

    <!-- Progress Indicator -->
    <div class="flex items-center justify-center mb-8">
      <div class="flex items-center">
        <div class={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500 dark:bg-dark-tertiary dark:text-dark-text-low'}`}>1</div>
        <div class={`w-16 h-1 mx-2 transition-all ${step >= 2 ? 'bg-primary' : 'bg-gray-200 dark:bg-dark-tertiary'}`}></div>
        <div class={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500 dark:bg-dark-tertiary dark:text-dark-text-low'}`}>2</div>
      </div>
    </div>

    <Card padding="lg" bordered>
      {#if flash.alert}
        <div class="mb-6 p-4 bg-error-light border border-error/30 rounded-xl text-error text-body2">
          {flash.alert}
        </div>
      {/if}

      <form onsubmit={handleSubmit}>
        {#if step === 1}
          <div class="space-y-5">
            <h2 class="text-h2 text-content mb-4">기본 정보</h2>

            <Input type="text" label="이름" placeholder="홍길동" bind:value={form.name} required error={errors.name} />

            <!-- 닉네임 필드 -->
            <div class="space-y-1.5">
              <label for="register-nickname" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium">
                닉네임 <span class="text-content-tertiary font-normal">(커뮤니티에서 표시되는 이름)</span>
              </label>
              <div class="flex gap-2">
                <div class="relative flex-1">
                  <input
                    id="register-nickname"
                    type="text"
                    bind:value={form.nickname}
                    placeholder="나만의 닉네임"
                    class={`w-full px-3 py-2 pr-8 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-surface text-content
                      ${nicknameStatus === 'available' ? 'border-green-400 dark:border-green-500' : nicknameStatus === 'taken' ? 'border-error dark:border-error' : 'border-gray-300 dark:border-dark-border'}`}
                  />
                  <!-- 상태 아이콘 -->
                  <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm pointer-events-none">
                    {#if nicknameStatus === 'checking'}
                      <svg class="w-4 h-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                    {:else if nicknameStatus === 'available'}
                      <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                      </svg>
                    {:else if nicknameStatus === 'taken'}
                      <svg class="w-4 h-4 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    {/if}
                  </span>
                </div>
                <button
                  type="button"
                  onclick={refreshNickname}
                  title="닉네임 랜덤 생성"
                  class="px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-border bg-surface text-content-tertiary hover:bg-gray-50 dark:hover:bg-dark-tertiary hover:text-primary transition-colors text-sm shrink-0"
                >
                  🎲 다시
                </button>
              </div>

              <!-- 상태 메시지 -->
              {#if nicknameStatus === 'available'}
                <p class="text-xs text-green-600 dark:text-green-400">사용 가능한 닉네임이에요</p>
              {:else if nicknameStatus === 'taken'}
                <div class="flex items-center gap-2 flex-wrap">
                  <p class="text-xs text-error">이미 사용 중인 닉네임이에요.</p>
                  {#if nicknameSuggestion}
                    <button
                      type="button"
                      onclick={applySuggestion}
                      class="text-xs text-primary hover:underline font-medium"
                    >
                      "{nicknameSuggestion}" 사용하기 →
                    </button>
                  {/if}
                </div>
              {:else if nicknameStatus === 'checking'}
                <p class="text-xs text-content-tertiary">확인 중...</p>
              {/if}
              {#if errors.nickname}
                <p class="text-xs text-error">{errors.nickname}</p>
              {/if}
            </div>

            <Input type="email" label="이메일" placeholder="you@email.com" bind:value={form.email} required error={errors.email} />

            <Input type="password" label="비밀번호" placeholder="최소 8자 이상" bind:value={form.password} required error={errors.password} />

            <Input
              type="password"
              label="비밀번호 확인"
              placeholder="비밀번호 재입력"
              bind:value={form.password_confirmation}
              required
              error={form.password && form.password_confirmation && form.password !== form.password_confirmation ? '비밀번호가 일치하지 않습니다' : undefined}
            />

            <Button type="button" onclick={nextStep} disabled={!canProceed} fullWidth>
              다음
            </Button>
          </div>

        {:else}
          <!-- Step 2: Organization Info -->
          <div class="space-y-5">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-h2 text-content">소속 정보</h2>
              <button type="button" onclick={prevStep} class="text-content-tertiary hover:text-gray-900 dark:hover:text-dark-text-high text-sm">← 이전</button>
            </div>

            <p class="text-body2 text-content-secondary mb-4">
              소속 인증을 위해 회사 이메일을 입력해주세요. 나중에 입력할 수도 있습니다.
            </p>

            <Input type="email" label="회사 이메일 (선택)" placeholder="you@company.com" bind:value={form.work_email} error={errors.work_email} />
            <Input type="text" label="회사/기관명 (선택)" placeholder="회사명" bind:value={form.organization_name} />

            <div class="grid grid-cols-2 gap-4">
              <Input type="text" label="부서 (선택)" placeholder="디지털혁신부" bind:value={form.department} />
              <Input type="text" label="팀 (선택)" placeholder="AX팀" bind:value={form.team} />
            </div>

            <Input type="text" label="직책 (선택)" placeholder="매니저" bind:value={form.position} />

            <p class="text-caption text-content-tertiary break-keep">
              부서와 팀은 둘 중 하나만 적어도 됩니다.<br />
              공개 여부는 가입 후 설정에서 정합니다.
            </p>

            <Button type="submit" fullWidth loading={isSubmitting} loadingLabel="가입 처리 중...">회원가입</Button>
          </div>
        {/if}
      </form>

      <p class="mt-6 text-center text-body2 text-content-tertiary">
        이미 계정이 있으신가요?
        <a href="/login" class="text-primary hover:text-primary-light font-semibold transition-colors">로그인</a>
      </p>
    </Card>
  </div>
</div>
